import { useRef, useState } from "react";
import { ChevronsLeft, ChevronsRight, GripVertical, HelpCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerCloseButton,
} from "@/components/ui/drawer";
import { SegmentedControl, SegmentedControlItem } from "@/components/ui/segmented-control";
import { OPEN_QUESTIONS } from "./openQuestions";
import { resetPrototype } from "./mock";
import {
  REVENUE_ACTIONS_VARIANTS,
  REVENUE_VARIANTS,
  useRevenueListVariant,
  type RevenueActionsVariant,
  type RevenueListVariant,
} from "./revenueVariant";
import {
  DOWNLOAD_VARIANTS,
  useDownloadVariant,
  type DownloadVariant,
} from "../statement-download/downloadVariant";
import { STATEMENT_DOWNLOAD_ONLY } from "../statement-download/shareScope";

/**
 * Prototype-only floating dev bar: the revenue list switch, the open
 * questions this prototype is asking, and the way back to the seeded data.
 *
 * One control per row, so another dial can be added without the bar running
 * across the screen, and Reset at the top because it is the way back from
 * whatever the dials below it did. It collapses to a handle for looking at a
 * screen it covers, and it is draggable; both the position and the collapsed
 * state are remembered.
 *
 * Mounted only while the dev auth bypass is active (see App.tsx), so it cannot
 * reach a real build.
 */
const POS_KEY = "col-dev-bar-pos";
const COLLAPSED_KEY = "col-dev-bar-collapsed";

function loadPos(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* fall through to default */
  }
  return { x: 12, y: 12 };
}

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota: the bar still works for this page view */
  }
}

export function PrototypeDevBar() {
  const { revenueVariant, setRevenueVariant, actionsVariant, setActionsVariant } =
    useRevenueListVariant();
  const { downloadVariant, setDownloadVariant } = useDownloadVariant();
  const [pos, setPos] = useState(loadPos);
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const [questionsOpen, setQuestionsOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const posRef = useRef(pos);
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    // Kept inside the viewport: dragged off the edge it would be unrecoverable
    // without clearing storage. Measured, because a collapsed bar is much smaller.
    const width = panel.current?.offsetWidth ?? 224;
    const height = panel.current?.offsetHeight ?? 40;
    const x = Math.max(0, Math.min(window.innerWidth - width, d.ox + (e.clientX - d.sx)));
    const y = Math.max(0, Math.min(window.innerHeight - height, d.oy + (e.clientY - d.sy)));
    posRef.current = { x, y };
    setPos(posRef.current);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (drag.current) store(POS_KEY, JSON.stringify(posRef.current));
    drag.current = null;
  };

  const toggleCollapsed = () => {
    setCollapsed((previous) => {
      store(COLLAPSED_KEY, String(!previous));
      return !previous;
    });
  };

  const handleReset = () => {
    resetPrototype();
    window.location.reload();
  };

  const dragHandle = (
    <button
      type="button"
      aria-label="Drag to move"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className="flex cursor-grab touch-none items-center px-0.5 text-muted-foreground active:cursor-grabbing"
    >
      <GripVertical className="size-4" />
    </button>
  );

  if (collapsed) {
    return (
      <div
        ref={panel}
        className="fixed z-[100] flex w-fit items-center rounded-md border border-border bg-background p-1 shadow-md"
        style={{ left: pos.x, top: pos.y }}
      >
        {dragHandle}
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          aria-label="Expand the dev bar"
          onClick={toggleCollapsed}
        >
          <ChevronsRight className="size-4 text-muted-foreground" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <div
        ref={panel}
        className="fixed z-[100] flex w-56 flex-col rounded-md border border-border bg-background shadow-md"
        style={{ left: pos.x, top: pos.y }}
      >
        <div className="flex items-center gap-1 border-b border-border p-1">
          {dragHandle}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="flex-1 justify-start pl-1"
            onClick={handleReset}
          >
            <RotateCcw className="size-4 text-muted-foreground" />
            Reset prototype
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            aria-label="Collapse the dev bar"
            onClick={toggleCollapsed}
          >
            <ChevronsLeft className="size-4 text-muted-foreground" />
          </Button>
        </div>

        {/* The shared statement-download build keeps only its own switch. */}
        {!STATEMENT_DOWNLOAD_ONLY && (
          <>
            <div className="flex flex-col gap-1.5 p-2">
              <span className="text-xs font-medium text-muted-foreground">Revenue</span>
              <SegmentedControl
                type="single"
                value={revenueVariant}
                onValueChange={(next) => next && setRevenueVariant(next as RevenueListVariant)}
                className="w-full"
              >
                {Object.entries(REVENUE_VARIANTS).map(([key, label]) => (
                  <SegmentedControlItem key={key} value={key} className="flex-1 px-2 py-1 text-xs">
                    {label}
                  </SegmentedControlItem>
                ))}
              </SegmentedControl>
            </div>

            {/* Only the tabs version has a tab to pick the button by. */}
            {revenueVariant === "tabs" && (
              <div className="flex flex-col gap-1.5 p-2 pt-0">
                <span className="text-xs font-medium text-muted-foreground">Revenue buttons</span>
                <SegmentedControl
                  type="single"
                  value={actionsVariant}
                  onValueChange={(next) => next && setActionsVariant(next as RevenueActionsVariant)}
                  className="w-full"
                >
                  {Object.entries(REVENUE_ACTIONS_VARIANTS).map(([key, label]) => (
                    <SegmentedControlItem
                      key={key}
                      value={key}
                      className="flex-1 px-2 py-1 text-xs"
                    >
                      {label}
                    </SegmentedControlItem>
                  ))}
                </SegmentedControl>
              </div>
            )}
          </>
        )}

        {/* The statement-download prototype: the statement preview's Download button. */}
        <div className="flex flex-col gap-1.5 border-t border-border p-2">
          <span className="text-xs font-medium text-muted-foreground">
            Statement Download button
          </span>
          <SegmentedControl
            type="single"
            value={downloadVariant}
            onValueChange={(next) => next && setDownloadVariant(next as DownloadVariant)}
            className="w-full"
          >
            {Object.entries(DOWNLOAD_VARIANTS).map(([key, label]) => (
              <SegmentedControlItem key={key} value={key} className="flex-1 px-2 py-1 text-xs">
                {label}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        </div>

        {!STATEMENT_DOWNLOAD_ONLY && (
          <div className="border-t border-border p-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start pl-1"
              onClick={() => setQuestionsOpen(true)}
            >
              <HelpCircle className="size-4 text-muted-foreground" />
              Open questions
            </Button>
          </div>
        )}
      </div>

      <Drawer open={questionsOpen} onOpenChange={setQuestionsOpen} direction="right">
        <DrawerContent>
          <DrawerHeader className="p-0 pb-6">
            <div className="flex flex-col gap-1">
              <DrawerTitle className="font-mono text-lg font-semibold">Open questions</DrawerTitle>
              <DrawerDescription>
                What this prototype does not decide yet — and who can answer it.
              </DrawerDescription>
            </div>
            <DrawerCloseButton />
          </DrawerHeader>
          <div className="flex flex-col gap-6">
            {OPEN_QUESTIONS.map((section) => (
              <section key={section.title} className="flex flex-col gap-2">
                <h3 className="font-mono text-sm font-medium">{section.title}</h3>
                <ul className="flex flex-col gap-2">
                  {section.questions.map((question) => (
                    <li
                      key={question}
                      className="border-l-2 border-border pl-3 text-sm text-muted-foreground"
                    >
                      {question}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
